import React, { useState, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { DateTime } from 'luxon';
import { Button, Typography } from "@material-ui/core";
import { useAppSelector } from "../../../../redux/hooks";
import { selectAccountSettings } from "../../../../redux/store";

import { CustomerLastAppointmentsBlockProps } from "./props";
import useStyles from "./css";
import { CalendarCheckmarkIcon, CalendarIcon } from "../../../../assets/icons";
import { Color } from "../../../../constants/colors";
import { CustomerLastAppointment } from "../../../../models/customer-appointment";


export default function CustomerLastAppointmentsBlock(props: CustomerLastAppointmentsBlockProps) {

  const { appointments } = props;

  const classes = useStyles();
  const { t } = useTranslation(["general"]);

  const accountSettings = useAppSelector(selectAccountSettings);
  const localeApp = useAppSelector(state => state.applicationInterface.locale);
  const dateFormat = t("last_appointments_date_format");
  const isClinicMode = accountSettings.isClinicMode;

  const [visibleAppointments, setVisibleAppointments] = useState<number>(3);
  const [expandedNotes, setExpandedNotes] = useState<{ [key: string]: boolean }>({});
  const [containerHeight, setContainerHeight] = useState<string | number>('auto');
  const listRef = useRef<HTMLDivElement>(null);

  const EvolutionNotesContent = ({ appointment }: { appointment: CustomerLastAppointment }) => {
    if (!(appointment.evolutionNotes && appointment.evolutionNotes.length > 0))
      return (<span>--</span>);

    return (
      <>
        {expandedNotes[appointment.id]
          ? appointment.evolutionNotes.map((note, index) => (
            <span key={index}>
              {note.replace(/<[^>]*>/g, '') + '\n'}
            </span>
          ))
          : appointment.evolutionNotes
            .join('; ')
            .replace(/<[^>]*>/g, '')
            .substring(0, 120)
        }

        {appointment.evolutionNotes
          .join(' ')
          .replace(/<[^>]*>/g, '')
          .length > 120 && (
            <span
              className={classes.seeMoreToggle}
              onClick={() => handleNoteExpansionToggle(appointment.id)}
            >
              {expandedNotes[appointment.id]
                ? ` ${t("See less.")}`
                : `...${t("See more.")}`
              }
            </span>
          )}
      </>
    );
  };

  const NotesContent = ({ appointment }: { appointment: CustomerLastAppointment }) => {
    if (!appointment.notes)
      return (<span>--</span>);

    const isExpanded = expandedNotes[appointment.id];
    const content = isExpanded
      ? removeHtmlTags(appointment.notes)
      : removeHtmlTags(appointment.notes).substring(0, 120);

    const toggleText = isExpanded
      ? ` ${t("See less.")}`
      : `...${t("See more.")}`;

    return (
      <>
        {content}
        {removeHtmlTags(appointment.notes).length > 120 && (
          <span
            className={classes.seeMoreToggle}
            onClick={() => handleNoteExpansionToggle(appointment.id)}
          >
            {toggleText}
          </span>
        )}
      </>
    );
  };

  const EmptyContent = () => {
    return (
      <div className={classes.emptyContentContainer}>
        <CalendarIcon style={{ width: 38, height: 41, color: Color.gray4 }} />
        <Typography className={classes.emptyContentTitle}>
          {t("There are no appointments scheduled previously.")}
        </Typography>
        <Typography className={classes.emptyContentBody}>
          {t("You can schedule an appointment now.")}
        </Typography>
      </div>
    );
  };

  const AppointmentsContent = () => {
    return (
      <div className={classes.appointmentsList} ref={listRef} style={{ maxHeight: containerHeight, overflowY: 'auto' }}>
        {appointments && appointments.slice(0, visibleAppointments).map((appointment) => (
          <div key={appointment.id} className={'appointment-card-container'}>
            <Typography className={classes.appointmentDate}>
              {DateTime.fromISO(appointment.startDate.toString())
                .setLocale(localeApp)
                .toFormat(dateFormat)}
            </Typography>

            <div className={classes.appointmentCard}>

              {appointment.services.map((service) => (
                <div key={service.id} className={classes.serviceItem}>
                  <Typography className={classes.serviceName}>{service.name}</Typography>
                  <Typography className={classes.serviceTime}>
                    {DateTime.fromISO(appointment.startDate.toString()).toFormat('HH:mm')} -
                    {DateTime.fromISO(appointment.startDate.toString()).plus({ minutes: service.duration || 0 }).toFormat('HH:mm')}
                  </Typography>
                  <Typography className={classes.serviceSpecialist}>{service.user}</Typography>
                </div>
              ))}

              <div className={classes.postServiceNotes}>

                <Typography className={classes.notesTitle}>
                  {accountSettings.isClinicMode
                    ? t("Evolution note:")
                    : t("Post-service notes:")
                  }
                </Typography>

                <Typography className={classes.notesContent}>

                  {isClinicMode
                    ? <EvolutionNotesContent appointment={appointment} />
                    : <NotesContent appointment={appointment} />
                  }

                </Typography>

              </div>
            </div>
          </div>
        ))}
        {appointments && appointments.length > 0 &&
          <Button
            className={`${classes.seeMoreButton} see-more-button`}
            onClick={() => setVisibleAppointments((prev) => prev + 3)}
            disabled={appointments.length < visibleAppointments || appointments.length <= 3}
          >
            <Typography className={classes.seeMoreButtonText}>
              {t("See more")}
            </Typography>
          </Button>
        }
      </div>
    );
  };

  useEffect(() => {
    setVisibleAppointments(3);
  }, [appointments]);

  useEffect(() => {
    if (listRef.current) {

      if (visibleAppointments <= 3) {
        setContainerHeight('auto');
      }

      else {
        const cards = listRef.current?.querySelectorAll('.appointment-card-container');
        let totalHeight = 0;

        const totalCards = cards.length;
        const startIndex = Math.max(0, totalCards - 3);

        for (let i = startIndex; i < totalCards; i++) {
          const card = cards[i];
          totalHeight += card.getBoundingClientRect().height;
        }

        totalHeight += 64;

        setContainerHeight(totalHeight);
      }
    }
  }, [visibleAppointments, expandedNotes]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [visibleAppointments, containerHeight]);

  function handleNoteExpansionToggle(appointmentId: string) {
    setExpandedNotes((prev) => ({
      ...prev,
      [appointmentId]: !prev[appointmentId],
    }));
  };

  function removeHtmlTags(text: string) {
    return text.replace(/<\/?[^>]+(>|$)/g, "");
  }

  function getAppointmentsTitle() {
    if (!appointments || appointments.length === 0) {
      return t("Last appointments");
    } else if (appointments.length === 1) {
      return t("Last scheduled appointment");
    } else {
      const count = Math.min(appointments.length, visibleAppointments || 3);
      return t("Last {{count}} appointments", { count });
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.titleContainer}>
        <div>
          <CalendarCheckmarkIcon style={{ width: 24, height: 24, color: Color.gray5 }} />
        </div>
        <Typography className={classes.title}>
          {t("Previous appointments details")}
        </Typography>
      </div>

      <div className={classes.appointmentsContainer}>
        <div className={classes.appointmentsHeader}>
          <Typography className={classes.title}>
            {getAppointmentsTitle()}
          </Typography>
        </div>

        {appointments && appointments.length > 0 ? <AppointmentsContent /> : <EmptyContent />}
      </div>
    </div>
  );
}
